<template>
  <Teleport to="body">
    <transition name="prompt-fade">
      <div v-if="open" class="prompt-modal-overlay" @click.self="cancel">
        <div class="prompt-modal-card" role="dialog" aria-modal="true">
          <h3 class="prompt-modal__title">{{ title }}</h3>
          <p v-if="description" class="prompt-modal__description">{{ description }}</p>
          <form @submit.prevent="confirm">
            <input
              ref="inputRef"
              v-model="value"
              :placeholder="placeholder"
              :maxlength="maxLength"
              class="prompt-modal__input"
              :aria-label="title"
            />
          </form>
          <div class="prompt-modal__actions">
            <button type="button" class="prompt-modal__btn prompt-modal__btn--ghost" @click="cancel">{{ cancelLabel }}</button>
            <button type="button" class="prompt-modal__btn prompt-modal__btn--primary" :disabled="!isValid" @click="confirm">{{ confirmLabel }}</button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'

export default defineComponent({
  name: 'PromptModal',
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, required: true },
    description: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    initialValue: { type: String, default: '' },
    confirmLabel: { type: String, default: 'Save' },
    cancelLabel: { type: String, default: 'Cancel' },
    maxLength: { type: Number, default: 120 },
  },
  emits: ['confirm', 'cancel'],
  setup(props, { emit }) {
    const value = ref(props.initialValue)
    const inputRef = ref<HTMLInputElement | null>(null)

    const isValid = ref(false)
    watch(value, v => { isValid.value = v.trim().length > 0 }, { immediate: true })

    watch(
      () => props.open,
      async isOpen => {
        if (isOpen) {
          value.value = props.initialValue
          await nextTick()
          inputRef.value?.focus()
          inputRef.value?.select()
        }
      }
    )

    function confirm() {
      if (!isValid.value) return
      emit('confirm', value.value.trim())
    }
    function cancel() {
      emit('cancel')
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.open) {
        e.preventDefault()
        cancel()
      }
    }
    onMounted(() => window.addEventListener('keydown', onKey))
    onUnmounted(() => window.removeEventListener('keydown', onKey))

    return { value, inputRef, isValid, confirm, cancel }
  },
})
</script>

<style scoped>
.prompt-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(10, 13, 25, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: var(--font-sans);
}

.prompt-modal-card {
  width: 100%;
  max-width: 440px;
  background: #1a2332;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 24px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.45);
  box-sizing: border-box;
}

.prompt-modal__title {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 700;
  color: #ffffff;
}
.prompt-modal__description {
  margin: 0 0 16px;
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
}

.prompt-modal__input {
  width: 100%;
  padding: 11px 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  font-family: var(--font-sans);
  font-weight: 300;
  transition: border-color 0.12s ease;
  box-sizing: border-box;
}
.prompt-modal__input:focus {
  outline: none;
  border-color: #96ffc9;
}

.prompt-modal__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 20px;
}

.prompt-modal__btn {
  padding: 9px 18px;
  border-radius: 10px;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.12s ease;
}

.prompt-modal__btn--ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  border-color: rgba(255, 255, 255, 0.15);
}
.prompt-modal__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.06);
}

.prompt-modal__btn--primary {
  background: #96ffc9;
  color: #181b2f;
}
.prompt-modal__btn--primary:hover:not(:disabled) {
  background: #82e8b0;
}
.prompt-modal__btn--primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.prompt-fade-enter-active,
.prompt-fade-leave-active {
  transition: opacity 0.15s ease;
}
.prompt-fade-enter-active .prompt-modal-card,
.prompt-fade-leave-active .prompt-modal-card {
  transition: transform 0.15s ease;
}
.prompt-fade-enter-from,
.prompt-fade-leave-to {
  opacity: 0;
}
.prompt-fade-enter-from .prompt-modal-card,
.prompt-fade-leave-to .prompt-modal-card {
  transform: translateY(8px) scale(0.98);
}
</style>
